import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import BrandSettingsDialog from "@/components/BrandSettingsDialog";
import AdminUsersDialog from "@/components/AdminUsersDialog";
import { getImpersonatedUser } from "@/lib/api";
import { LayoutDashboard, Users, Route as RouteIcon, ClipboardPaste, LogOut, Truck } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/drivers", label: "Drivers", icon: Users, testId: "nav-drivers" },
  { to: "/routes", label: "Routes", icon: RouteIcon, testId: "nav-routes" },
  { to: "/vans", label: "Vans", icon: Truck, testId: "nav-vans" },
  { to: "/paste", label: "Paste", icon: ClipboardPaste, testId: "nav-paste" },
];

function BrandMark({ compact = false }) {
  const { settings } = useSettings();
  const name = settings.app_name || "Maan group";
  const size = compact ? "w-6 h-6" : "w-9 h-9";
  return (
    <div className="flex items-center gap-2 min-w-0">
      {settings.logo_data_url ? (
        <img
          src={settings.logo_data_url}
          alt={name}
          className={`${size} object-contain rounded-md bg-white`}
          data-testid="brand-logo"
        />
      ) : (
        <div className={`${size} bg-[#2563EB] flex items-center justify-center rounded-md`}>
          <Truck className={compact ? "w-3.5 h-3.5 text-white" : "w-5 h-5 text-white"} />
        </div>
      )}
      <div className="min-w-0">
        <div className={`font-heading font-semibold leading-tight truncate ${compact ? "text-sm" : "text-base"}`} data-testid="brand-name">{name}</div>
        {!compact && <div className="text-[11px] text-slate-500 leading-tight">Route Registry</div>}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function onLogout() {
    await logout();
    nav("/login");
  }

  return (
    <div className="min-h-screen flex bg-[#F7F7F9]">
      <aside className="hidden md:flex flex-col w-60 border-r border-[#E2E2E8] bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-[#E2E2E8]">
          <BrandMark />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive ? "bg-blue-50 text-[#2563EB] font-medium" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E2E2E8] space-y-0.5">
          <AdminUsersDialog />
          <BrandSettingsDialog />
          {getImpersonatedUser() && (
            <div className="px-3 py-1.5 text-xs bg-amber-50 border border-amber-200 rounded text-amber-800 font-mono-data" data-testid="impersonation-banner">
              Viewing another user
            </div>
          )}
          <div className="px-3 py-2 text-xs text-slate-500 truncate" data-testid="current-user-email">{user?.email}</div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors duration-150"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-[#E2E2E8] z-30">
        <div className="flex items-center justify-between px-4 py-3 gap-2">
          <BrandMark compact />
          <div className="flex items-center gap-1">
            <BrandSettingsDialog
              trigger={
                <button data-testid="open-brand-settings-mobile" className="p-2 text-slate-500 hover:text-slate-900">
                  <span className="sr-only">Settings</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                </button>
              }
            />
            <button onClick={onLogout} data-testid="logout-button-mobile" className="p-2 text-slate-500 hover:text-slate-900"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex overflow-x-auto border-t border-[#E2E2E8]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testId}-mobile`}
              className={({ isActive }) =>
                `flex-1 text-center text-xs py-2 border-b-2 transition-colors duration-150 whitespace-nowrap ${
                  isActive ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 md:pt-0 pt-[92px] fade-in">
        <Outlet />
      </main>
    </div>
  );
}
